/**
 * @file ClientReports.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useState } from "react";
import SkinPage from "../../SkinPage";
import Widget from "../../widgets/Widget";
import { getFromSession } from "../../../../utils/sessionUtils";
import Request from "../../../../utils/fetchUtils";
import { Grid } from "@mui/material";
import SelectInput from "../../../../components/forms/SelectInput";
import TextInput from "../../../../components/forms/TextInput";
import { useForm } from "../../../../utils/useForm";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";

function ClientReports() {
  const brokerAccounts = getFromSession("brokerAccounts") ?? [];
  const tradierAccount = brokerAccounts.find(
    (acc) => acc.provider === "tradier"
  );
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters, resetFilters] = useForm({
    type: "",
    start: "",
    end: "",
  });
  const handleGetReport = async () => {
    const { type, start, end } = filters;
    if (type !== "" && start !== "" && end !== "") {
      setIsLoading(true);
      const res = await Request(
        `trading/${tradierAccount.id}/documents?type=${type}&start=${start}&end=${end}`
      );
      setIsLoading(false);
      if (res.ok) {
        setReports(res.data);
        resetFilters();
      } else {
        Swal.fire(
          "Error",
          res?.body?.error?.errors?.[0] || "unknown error",
          "error"
        );
      }
    } else {
      Swal.fire(
        "Oops",
        "You need to fill all the fields to get your report",
        "warning"
      );
    }
  };

  const handleResetFilters = () => {
    resetFilters();
    setReports([]);
  };

  useState(() => {}, []);

  return (
    <SkinPage>
      {isLoading && <Loader />}
      <Widget>
        <Grid container columnSpacing={{ xs: 3, sm: 3, md: 3 }}>
          <Grid item xs={3}>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Report type",
                  name: "type",
                  action: setFilters,
                  value: filters.type,
                }}
                data={[
                  { value: "STATEMENT", text: "Statement" },
                  { value: "TAX", text: "Tax" },
                  { value: "CONFIRM", text: "Confirm" },
                ]}
              />
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Start date",
                  type: "date",
                  name: "start",
                  value: filters.start,
                  action: setFilters,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "End date",
                  type: "date",
                  name: "end",
                  value: filters.end,
                  action: setFilters,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={3}>
            <div style={{ textAlign: "right", margin: "30px 0" }}>              <button
                className="primary small"
                style={{ width: "100%" }}
                onClick={handleGetReport}
              >
                Create Report
              </button>
              <button
                className="reset-filters-btn full-width"
                onClick={handleResetFilters}
              >
                Reset Filters
              </button>
            </div>
          </Grid>
        </Grid>
        <div>
          {reports.map((r) => (
            <div>
              <embed src={r.url} type="pdf" />
            </div>
          ))}
        </div>
      </Widget>
    </SkinPage>
  );
}

export default ClientReports;
